import React, { useMemo, useState } from "react";
import { Button, InputNumber, Radio, Select, Tag, Tooltip } from "antd";
import {
  ArrowRightOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { monthNames } from "../../../../../utils/constants";

const MODE_LABEL = { multiple: "groupée", unique: "par prestation" };
const monthKey = r => `${r.year}-${String(r.month).padStart(2, "0")}`;
const monthLabel = r => `${monthNames[r.month - 1] || r.month} ${r.year}`;
const byMonth = (a, b) => a.year - b.year || a.month - b.month;
const nextMonth = r => ({
  ...r,
  year: r.month >= 12 ? r.year + 1 : r.year,
  month: r.month >= 12 ? 1 : r.month + 1,
});

/** Before-lists per institution when the server did not send `perSource` (older preview). */
const derivePerSource = (timeline, sources = [], existingTarget) => {
  const all = existingTarget
    ? [
        {
          _id: existingTarget._id,
          name: `${existingTarget.name} (cible actuelle)`,
        },
        ...sources,
      ]
    : sources;
  return all.map(s => {
    const rules = [];
    for (const t of timeline) {
      const opts = t.conflict
        ? t.options || []
        : [{ type: t.type, sources: t.sources || [] }];
      for (const o of opts)
        if ((o.sources || []).some(x => String(x._id) === String(s._id)))
          rules.push({ year: t.year, month: t.month, type: o.type });
    }
    return { sourceId: s._id, sourceName: s.name, rules };
  });
};

const MonthTag = ({ status }) =>
  status === "pending" ? (
    <Tag
      className="cfg-tag cfg-tag--pending"
      icon={<ExclamationCircleOutlined />}
    >
      à choisir
    </Tag>
  ) : status === "added" ? (
    <Tag className="cfg-tag cfg-tag--typed">ajoutée</Tag>
  ) : status === "manual" ? (
    <Tag className="cfg-tag cfg-tag--manual">choix manuel</Tag>
  ) : (
    <Tag className="cfg-tag cfg-tag--same">inchangée</Tag>
  );

/**
 * Billing rules as a pair. AVANT: the dated rules of every establishment. APRÈS: the merged,
 * editable chronology — one line per month (`div.cfg-month[data-decide]`, radios « groupée /
 * par prestation »), plus « Ajouter une règle ». Every edit sends the whole list; months still
 * without a mode stay visible but are not sent.
 */
export function BillingPair({
  bl,
  sources,
  existingTarget,
  decided,
  disabled,
  onSave,
  stacked,
}) {
  const timeline = bl.timeline || [];
  const conflicts = bl.conflicts || [];
  const union = useMemo(() => new Map(timeline.map(t => [monthKey(t), t])), [
    timeline,
  ]);
  const rows = useMemo(() => {
    const base = (Array.isArray(decided) ? decided : timeline).map(r => ({
      year: r.year,
      month: r.month,
      type: MODE_LABEL[r.type] ? r.type : null,
    }));
    // a conflict month can never disappear: the server refuses to apply until it has a mode
    for (const c of conflicts)
      if (!base.some(r => r.year === c.year && r.month === c.month))
        base.push({ year: c.year, month: c.month, type: null });
    return base.sort(byMonth);
  }, [decided, timeline, conflicts]);
  const perSource =
    bl.perSource || derivePerSource(timeline, sources, existingTarget);

  const send = list =>
    onSave(
      list
        .filter(r => MODE_LABEL[r.type])
        .map(r => ({ year: r.year, month: r.month, type: r.type }))
        .sort(byMonth),
    );
  const setMode = (row, type) =>
    send(rows.map(r => (r === row ? { ...r, type } : r)));
  const remove = row => send(rows.filter(r => r !== row));

  const [add, setAdd] = useState(() => {
    const last = rows[rows.length - 1];
    if (last) return nextMonth({ ...last, type: last.type || "unique" });
    const now = new Date();
    return nextMonth({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      type: "unique",
    });
  });
  const addRule = () => {
    send([
      ...rows.filter(r => !(r.year === add.year && r.month === add.month)),
      { year: add.year, month: add.month, type: add.type },
    ]);
    setAdd(nextMonth(add));
  };

  const pendingCount = rows.filter(r => {
    const t = union.get(monthKey(r));
    return t && t.conflict && !r.type;
  }).length;

  const before = (
    <div className="cfg-card cfg-card--before cfg-bill">
      <div className="cfg-head cfg-head--before">
        {stacked && <div className="sv-ribbon sv-ribbon--before">AVANT</div>}
        <div className="cfg-head-title">Avant — par établissement</div>
        <div className="cfg-head-sub">
          les règles datées que chaque établissement a aujourd'hui
        </div>
      </div>
      <div className="cfg-bill-sources">
        {perSource.map(s => (
          <div key={s.sourceId} className="cfg-bill-source">
            <div className="cfg-bill-source-name">{s.sourceName}</div>
            {s.rules && s.rules.length ? (
              [...s.rules].sort(byMonth).map(r => (
                <div key={monthKey(r)} className="cfg-bill-rule">
                  <span className="cfg-who">{monthLabel(r)}</span>
                  <ArrowRightOutlined className="cfg-arrow" />
                  <span className="sv-val">{MODE_LABEL[r.type] || r.type}</span>
                </div>
              ))
            ) : (
              <div className="cfg-bill-rule">
                <span className="sv-empty">aucune règle datée</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const after = (
    <div className="cfg-card cfg-card--after cfg-bill">
      <div className="cfg-head cfg-head--after">
        {stacked && <div className="sv-ribbon sv-ribbon--after">APRÈS</div>}
        <div className="cfg-head-title">Après — chronologie fusionnée</div>
        <div className="cfg-head-sub">
          {rows.length
            ? `${rows.length} mois dans la chronologie${
                pendingCount
                  ? ` · ${pendingCount} à choisir`
                  : conflicts.length
                  ? " · conflits tranchés"
                  : ""
              }`
            : "aucune règle datée : le mode « Facturation groupée » ci-dessus s'applique"}
        </div>
      </div>
      <div className="cfg-lines">
        {rows.map(r => {
          const t = union.get(monthKey(r));
          const locked = !!(t && t.conflict);
          const status =
            t && t.conflict && !r.type
              ? "pending"
              : !t
              ? "added"
              : t.conflict || t.type !== r.type
              ? "manual"
              : "same";
          return (
            <div
              key={monthKey(r)}
              className={`cfg-month cfg-month--${status}`}
              data-decide=""
              data-month={monthKey(r)}
            >
              <div className="cfg-month-top">
                <span className="cfg-month-label">{monthLabel(r)}</span>
                <Radio.Group
                  disabled={disabled}
                  value={r.type}
                  onChange={e => setMode(r, e.target.value)}
                >
                  <Radio value="multiple">groupée</Radio>
                  <Radio value="unique">par prestation</Radio>
                </Radio.Group>
                <span className="cfg-month-end">
                  <MonthTag status={status} />
                  <Tooltip
                    title={
                      locked
                        ? "mois en conflit : un mode doit être choisi"
                        : "Retirer cette règle"
                    }
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={disabled || locked}
                      onClick={() => remove(r)}
                    />
                  </Tooltip>
                </span>
              </div>
              <div className="cfg-holders">
                {t && t.conflict
                  ? (t.options || [])
                      .map(
                        o =>
                          `${MODE_LABEL[o.type] || o.type} : ${(o.sources || [])
                            .map(s => s.name)
                            .join(", ")}`,
                      )
                      .join(" · ")
                  : t
                  ? `vient de : ${(t.sources || [])
                      .map(s => s.name)
                      .join(", ")}`
                  : "règle ajoutée manuellement"}
              </div>
            </div>
          );
        })}
        {!rows.length && (
          <div className="cfg-bill-none">
            Aucune règle datée — rien à fusionner ici. Vous pouvez en ajouter
            une ci-dessous.
          </div>
        )}
        <div className="cfg-bill-add">
          <span className="cfg-bill-add-label">Ajouter un mois :</span>
          <Select
            disabled={disabled}
            style={{ width: 130 }}
            value={add.month}
            options={monthNames.map((m, i) => ({ value: i + 1, label: m }))}
            onChange={month => setAdd({ ...add, month })}
          />
          <InputNumber
            disabled={disabled}
            style={{ width: 90 }}
            min={2000}
            max={2100}
            value={add.year}
            onChange={year => setAdd({ ...add, year: year || add.year })}
          />
          <Radio.Group
            disabled={disabled}
            value={add.type}
            onChange={e => setAdd({ ...add, type: e.target.value })}
          >
            <Radio value="multiple">groupée</Radio>
            <Radio value="unique">par prestation</Radio>
          </Radio.Group>
          <Button disabled={disabled} icon={<PlusOutlined />} onClick={addRule}>
            Ajouter une règle
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`cfg-duo${stacked ? " cfg-duo--stacked" : ""}`}>
      {before}
      {after}
    </div>
  );
}
